import React, { useState, useEffect } from 'react';
import CustomPagination from 'components/common/pagination/pagination';
import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import styles from './SystemsOverview.module.scss';
import commonStyles from 'styles/common.module.scss';
import { commonGridStyles } from 'styles/gridStyles';
import { Dayjs } from 'dayjs';
import { statisticsService } from 'components/api/statistics';

interface SystemsOverviewProps {
  theme?: string;
  siteId?: string | number;
  fromDate?: Dayjs | null;
  toDate?: Dayjs | null;
}

const SystemsOverview: React.FC<SystemsOverviewProps> = ({
  theme = 'dark',
  siteId,
  fromDate,
  toDate,
}) => {
  const isDark = theme === 'dark';

  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentLimit, setPaymentLimit] = useState(10);
  const [paymentRows, setPaymentRows] = useState<any[]>([]);
  const [paymentTotal, setPaymentTotal] = useState(0);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [gamePage, setGamePage] = useState(1);
  const [gameLimit, setGameLimit] = useState(10);
  const [gameRows, setGameRows] = useState<any[]>([]);
  const [gameTotal, setGameTotal] = useState(0);
  const [gameLoading, setGameLoading] = useState(false);
  const fromStr = fromDate?.toISOString();
  const toStr = toDate?.toISOString();

  useEffect(() => {
    if (!siteId || siteId === 'all' || !fromStr || !toStr) return;

    let isMounted = true;
    const timeoutId = setTimeout(async () => {
      setPaymentLoading(true);
      try {
        const response = await statisticsService.getPaymentSystems({
          siteId,
          from: fromStr,
          to: toStr,
          page: paymentPage,
          limit: paymentLimit,
          sortField: 'id',
          sortValue: 'DESC',
        });
        if (isMounted) {
          setPaymentRows(response.data?.docs || []);
          setPaymentTotal(response.data?.totalDocs || 0);
        }
      } catch (error) {
        if (isMounted) console.error('Failed to fetch payment systems:', error);
      } finally {
        if (isMounted) setPaymentLoading(false);
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [siteId, fromStr, toStr, paymentPage, paymentLimit]);

  useEffect(() => {
    if (!siteId || siteId === 'all' || !fromStr || !toStr) return;

    let isMounted = true;
    const timeoutId = setTimeout(async () => {
      setGameLoading(true);
      try {
        const response = await statisticsService.getGameSystems({
          siteId,
          from: fromStr,
          to: toStr,
          page: gamePage,
          limit: gameLimit,
          sortField: 'id',
          sortValue: 'DESC',
        });
        if (isMounted) {
          setGameRows(response.data?.docs || []);
          setGameTotal(response.data?.totalDocs || 0);
        }
      } catch (error) {
        if (isMounted) console.error('Failed to fetch game systems:', error);
      } finally {
        if (isMounted) setGameLoading(false);
      }
    }, 100);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [siteId, fromStr, toStr, gamePage, gameLimit]);

  const renderValueCell = (params: any) => {
    const value = params.value;
    if (value === undefined || value === null) return '-';

    const isNegative =
      typeof value === 'number' ? value < 0 : typeof value === 'string' && value.startsWith('-');
    const displayValue = isNegative
      ? typeof value === 'string'
        ? value.substring(1)
        : Math.abs(value as number)
      : value;

    return (
      <span style={{ color: isNegative ? '#ff4d4f' : 'inherit' }}>
        {isNegative ? '-' : ''}
        {displayValue}
      </span>
    );
  };

  const paymentColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'deposits', headerName: 'Deposits', width: 150, renderCell: renderValueCell },
    { field: 'withdrawals', headerName: 'Withdrawals', width: 150, renderCell: renderValueCell },
    { field: 'commission', headerName: 'Commission', width: 150, renderCell: renderValueCell },
    { field: 'profit', headerName: 'Profit', width: 150, renderCell: renderValueCell },
  ];

  const gameColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'bet', headerName: 'Bet', width: 150, renderCell: renderValueCell },
    { field: 'win', headerName: 'Win', width: 150, renderCell: renderValueCell },
    { field: 'different', headerName: 'Different', width: 150, renderCell: renderValueCell },
    { field: 'fee', headerName: 'Fee', width: 150, renderCell: renderValueCell },
  ];
  return (
    <Box className={styles.systemsContainer}>
      <Box className={styles.systemSection}>
        <Typography className={styles.sectionTitle} sx={{ color: isDark ? '#fff' : '#1a2c38' }}>
          Payment systems
        </Typography>
        <Box className={commonStyles.tableWrapper}>
          <DataGrid
            rows={paymentRows}
            rowCount={paymentTotal}
            columns={paymentColumns}
            sx={commonGridStyles}
            loading={paymentLoading}
            getRowId={(row) => row.paymentId}
            hideFooter
          />
          <CustomPagination
            count={paymentTotal}
            page={paymentPage - 1}
            onPageChange={(_, newPage) => setPaymentPage(newPage + 1)}
            rowsPerPage={paymentLimit}
            onRowsPerPageChange={(e) => {
              setPaymentLimit(parseInt(e.target.value, 10));
              setPaymentPage(1);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </Box>
      </Box>

      <Box className={styles.systemSection}>
        <Typography className={styles.sectionTitle} sx={{ color: isDark ? '#fff' : '#1a2c38' }}>
          Game systems
        </Typography>
        <Box className={commonStyles.tableWrapper}>
          <DataGrid
            rows={gameRows}
            rowCount={gameTotal}
            columns={gameColumns}
            sx={commonGridStyles}
            loading={gameLoading}
            getRowId={(row) => row.subProviderId}
            hideFooter
          />
          <CustomPagination
            count={gameTotal}
            page={gamePage - 1}
            onPageChange={(_, newPage) => setGamePage(newPage + 1)}
            rowsPerPage={gameLimit}
            onRowsPerPageChange={(e) => {
              setGameLimit(parseInt(e.target.value, 10));
              setGamePage(1);
            }}
            rowsPerPageOptions={[5, 10, 20]}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SystemsOverview;
